import {
  laneSpokenTextAt,
  laneSpeechEndedAt,
  laneTimingState,
  publishLaneTiming,
  type PlaybackProfile,
} from '../lane-timing';
export {
  clearLaneTiming,
  laneTimingState,
  markLaneSpokenText,
  setLaneTimingEnabled,
} from '../lane-timing';

// Keep this threshold identical to the browser audio tap used by Gate R.
export const AUDIO_SILENCE_THRESHOLD = 10 ** (-50 / 20);

type Part = { first: number; last: number };
const parts = new Map<number, Part>();
let firstAudioAt: number | null = null;
let maxGapMs: number | null = null;

function nonSilent(buffer: AudioBuffer) {
  let first = -1;
  let last = -1;
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, channel) =>
    buffer.getChannelData(channel),
  );
  for (let sample = 0; sample < buffer.length; sample += 1) {
    for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
      if (Math.abs(channels[channel][sample]) > AUDIO_SILENCE_THRESHOLD) {
        first = first < 0 ? sample : first;
        last = sample;
        break;
      }
    }
  }
  return first < 0
    ? null
    : { first: first / buffer.sampleRate, last: (last + 1) / buffer.sampleRate };
}

export function resetLanePlaybackTiming() {
  parts.clear();
  firstAudioAt = null;
  maxGapMs = null;
}

export function observeScheduledAudio(input: {
  index: number;
  buffer: AudioBuffer;
  scheduledAt: number;
  contextNow: number;
  observedAt?: number;
  profile?: PlaybackProfile | null;
}) {
  if (!laneTimingState().enabled) return;
  const active = nonSilent(input.buffer);
  if (!active) return;
  const first = input.scheduledAt + active.first;
  const last = input.scheduledAt + active.last;
  const previous = parts.get(input.index);
  parts.set(input.index, {
    first: previous ? Math.min(previous.first, first) : first,
    last: previous ? Math.max(previous.last, last) : last,
  });
  const prior = [...parts.entries()]
    .filter(([index]) => index < input.index)
    .sort((a, b) => b[0] - a[0])[0]?.[1];
  // Only the first audible chunk starts a part; later PCM chunks extend its end.
  const gapMs = !previous && prior ? Math.max(0, (first - prior.last) * 1000) : undefined;
  if (gapMs !== undefined) maxGapMs = Math.max(maxGapMs ?? 0, gapMs);
  const observedAt = input.observedAt ?? performance.now();
  const firstScheduledAt = observedAt + (first - input.contextNow) * 1000;
  if (firstAudioAt === null) {
    firstAudioAt = firstScheduledAt;
    const spokenAt = laneSpokenTextAt();
    publishLaneTiming({
      firstAudioMs: spokenAt === null ? null : Math.max(0, firstScheduledAt - spokenAt),
      ttsToAudioMs: spokenAt === null ? null : Math.max(0, firstScheduledAt - spokenAt),
      totalMs:
        laneSpeechEndedAt() === null ? null : Math.max(0, firstScheduledAt - laneSpeechEndedAt()!),
    });
  }
  publishLaneTiming({
    partGapMs: gapMs,
    maxPartGapMs: maxGapMs,
    playbackProfile: input.profile ?? undefined,
  });
}
